import { ConfluencePageAri } from '@atlassian/ari';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { CompanyHubAnnouncementBannerQuery } from './CompanyHubAnnouncementBannerQuery.graphql.ts';
import { CompanyHubAnnouncementBannerStatusQuery } from './CompanyHubAnnouncementBannerStatusQuery.graphql.ts';
import type {
	CompanyHubAnnouncementBannerQuery as CompanyHubAnnouncementBannerQueryType,
	CompanyHubAnnouncementBannerQueryVariables,
} from './__types__/CompanyHubAnnouncementBannerQuery.ts';
import type {
	CompanyHubAnnouncementBannerStatusQuery as CompanyHubAnnouncementBannerStatusQueryType,
	CompanyHubAnnouncementBannerStatusQueryVariables,
} from './__types__/CompanyHubAnnouncementBannerStatusQuery.ts';

export const COMPANY_HUB_ANNOUNCEMENT_BANNER_CONTENT_PROPERTY_KEY =
	'companyHubAnnouncementBannerId';

export type CompanyHubAnnouncementBanner = {
	bannerId: string | null;
	bannerStatus?: string;
};

export const getCompanyHubAnnouncementBanner = async (
	contentId: string,
	tenantId: string,
): Promise<CompanyHubAnnouncementBanner> => {
	try {
		// Fetch the announcement banner id from the page properties
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data: dataAnnouncementBanner } = await getApolloClient().query<
			CompanyHubAnnouncementBannerQueryType,
			CompanyHubAnnouncementBannerQueryVariables
		>({
			query: CompanyHubAnnouncementBannerQuery,
			variables: {
				pageId: ConfluencePageAri.create({ siteId: tenantId, pageId: contentId }).toString(),
				keys: [COMPANY_HUB_ANNOUNCEMENT_BANNER_CONTENT_PROPERTY_KEY],
			},
		});

		const bannerId = dataAnnouncementBanner?.confluence?.page?.properties?.[0]?.value;
		if (!bannerId) {
			return {
				bannerId: null,
			};
		}

		// If the announcement banner exists check banner status
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data: dataAnnouncementBannerStatus } = await getApolloClient().query<
			CompanyHubAnnouncementBannerStatusQueryType,
			CompanyHubAnnouncementBannerStatusQueryVariables
		>({
			query: CompanyHubAnnouncementBannerStatusQuery,
			variables: {
				bannerId,
			},
		});

		return {
			bannerId,
			bannerStatus: dataAnnouncementBannerStatus?.adminAnnouncementBannerSetting?.status,
		};
	} catch (error) {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PERFORMANCE_INSIGHTS,
		});
		markErrorAsHandled(error);

		return {
			bannerId: null,
		};
	}
};
